<template>
  <lb-drawer
    data-testid="transaction-matching-config-new__add-edit-rule-drawer"
    :value="value"
    @input="$emit('input', $event)"
    :heading="addeditdata.__type === 'add' ? 'Add Rule' : 'Edit Rule'"
    width="60%"
    :loading="loading"
  >
    <template v-slot:body>
      <div class="add-rule-body">

        <!--  Custom Stepper Header  -->
        <div class="add-rule-stepper-header mb-5">
          <div class="add-rule-stepper-steps">
            <template v-for="(s, i) in steps">
              <div
                :key="s.id"
                class="add-rule-stepper-step"
                :class="{ 'is-done': currentStep > s.id, 'is-active': currentStep === s.id }"
                :style="currentStep > s.id ? 'cursor:pointer' : ''"
                @click="currentStep > s.id ? (currentStep = s.id) : null"
              >
                <div class="add-rule-step-circle" :class="{ 'arc-done': currentStep > s.id, 'arc-active': currentStep === s.id }">
                  <v-icon v-if="currentStep > s.id" small color="white">mdi-check</v-icon>
                  <span v-else>{{ s.id }}</span>
                </div>
                <span class="add-rule-step-label" :class="{ 'lbl-active': currentStep === s.id, 'lbl-done': currentStep > s.id }">
                  {{ s.label }}
                </span>
              </div>
              <div v-if="i < steps.length - 1" :key="'d-' + s.id" class="add-rule-step-connector" :class="{ 'conn-done': currentStep > s.id }" />
            </template>
          </div>
        </div>

        <!--  STEP 1 — Match Setup  -->
        <div v-if="currentStep === 1">

          <!-- Entities -->
          <div class="mb-6">
            <div class="d-flex align-center justify-space-between mb-2">
              <div>
                <div class=" font-weight-semibold">Dataset to Match</div>
                <div class=" grey--text">You can only select <strong>2 datasets</strong> to match</div>
              </div>
              <v-btn v-if="fileconfig1 || fileconfig2" text small color="grey darken-1" @click="fileconfig1 = ''; fileconfig2 = ''">Clear</v-btn>
            </div>

            <div v-if="analysisloading" class="d-flex justify-center py-4">
              <v-progress-circular indeterminate size="24" color="primary" />
            </div>
            <div v-else-if="entities.length === 0" class=" grey--text text-center py-3">
              No datasets found. Ensure analysis data is loaded.
            </div>
            <v-row v-else dense>
              <v-col v-for="ent in entities" :key="ent.id" cols="4">
                <v-card
                  outlined
                  class="add-rule-entity-card pa-3 d-flex align-center"
                  :class="{
                    'ent-first':    fileconfig1 === ent.id,
                    'ent-second':   fileconfig2 === ent.id,
                    'ent-disabled': bothEntitiesSelected && fileconfig1 !== ent.id && fileconfig2 !== ent.id
                  }"
                  :disabled="bothEntitiesSelected && fileconfig1 !== ent.id && fileconfig2 !== ent.id"
                  @click="handleEntityClick(ent.id)"
                  data-testid="add-rule__entity-card"
                >
                  <div class="add-rule-entity-badge" :class="{ 'badge-1': fileconfig1 === ent.id, 'badge-2': fileconfig2 === ent.id }">
                    <span class=" font-weight-bold">{{ fileconfig1 === ent.id ? '1' : fileconfig2 === ent.id ? '2' : '' }}</span>
                  </div>
                  <span class="pa-0 radius-off font-weight-medium add-rule-entity-label">{{ ent.label }}</span>
                </v-card>
              </v-col>
            </v-row>

          </div>

          <v-divider class="mb-5" />

          <!-- Match Behavior -->
          <div>
            <div class=" font-weight-semibold mb-3">Match Behavior</div>
            <v-row>
              <v-col cols="6">
                <div class="d-flex align-center gap-1 grey--text font-weight-medium mb-2">
                  Match Flag
                  <v-tooltip right content-class="tooltip-right" max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <div>
                      <p class="mb-2"><strong>Automated Matching:</strong> In the case of automated matching, a predefined rule is applied to automatically consider transactions (line-items) as matched. Once the rule conditions are met, the transactions are automatically paired without requiring manual intervention or approval.</p>
                      <p class="mb-0"><strong>Suggested Matching:</strong> In the case of suggested matching, a set of rules is used to suggest potential matches between transactions (line-items). However, these suggested matches go through a process of approval before being considered final. The suggested matches are presented to users for review and validation, allowing them to confirm or modify the proposed matches based on their expertise and judgment.</p>
                    </div>
                  </v-tooltip>
                </div>
                <v-radio-group v-model="localNature" hide-details class="mt-0 pt-0">
                  <v-radio
                    v-for="flag in rulenaturelist"
                    :key="flag.value"
                    :label="flag.name"
                    :value="flag.value"
                    color="primary"
                    data-testid="add-rule__match-flag-option"
                    class="add-rule-opt-card"
                  />
                </v-radio-group>
              </v-col>
              <v-col cols="6">
                <div class="d-flex align-center gap-1 grey--text font-weight-medium mb-2">
                  Relation Type
                  <v-tooltip right content-class="tooltip-right" max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <div>
                      <p class="mb-2"><strong>1:1 Matching:</strong> This scenario involves matching one transaction (line-item) with another transaction. Each transaction is paired with another transaction, resulting in a one-to-one relationship.</p>
                      <p class="mb-2"><strong>1:M Matching:</strong> In this scenario, one transaction (line-item) is matched with a group of transactions. The transaction is associated with multiple related transactions, resulting in a one-to-many relationship.</p>
                      <p class="mb-2"><strong>M:1 Matching:</strong> In the M:1 scenario, a group of transactions (line-items) is matched with one transaction. Multiple transactions are associated with single transactions, resulting in a many-to-one relationship.</p>
                      <p class="mb-0"><strong>M:M Matching:</strong> This scenario involves matching a group of transactions (line-items) with another group of transactions. Each transaction within one group is paired with multiple transactions within another group, resulting in a many-to-many relationship.</p>
                    </div>
                  </v-tooltip>
                </div>
                <v-radio-group v-model="localType" hide-details class="mt-0 pt-0">
                  <v-radio
                    v-for="rel in ruletypelist"
                    :key="rel.value"
                    :label="rel.name"
                    :value="rel.value"
                    color="primary"
                    data-testid="add-rule__relation-type-option"
                    class="add-rule-opt-card"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </div>
        </div>

        <!--  STEP 2 — Matching Rules  -->
        <div v-if="currentStep === 2">

          <!-- Step 2 Context Badge -->
          <div class="add-rule-context-badge mb-5">
            <span class="add-rule-context-label  text-uppercase font-weight-medium">Rule Type</span>
            <span class="add-rule-context-dot">·</span>
            <template v-if="fileconfig1 && fileconfig2">
              <span class="">
                <strong class="add-rule-context-ent">{{ entityLabel(fileconfig1) }}</strong>
                <v-icon x-small class="mx-1">mdi-arrow-left-right</v-icon>
                <strong class="add-rule-context-ent">{{ entityLabel(fileconfig2) }}</strong>
              </span>
              <span class="add-rule-context-dot">·</span>
            </template>
            <v-chip small label dark color="primary" class="font-weight-bold">{{ localType }}</v-chip>
          </div>

          <!-- String Rules -->
          <div class="mb-5">
            <div class="d-flex align-center justify-space-between mb-3">
              <div>
                <div class=" font-weight-semibold">String Matching Rules</div>
                <!-- <div class=" grey--text mt-1">Define exact or fuzzy field matches</div> -->
              </div>
              <div class="d-flex align-center gap-3">
                <!-- <v-chip small label outlined color="grey">AND logic</v-chip> -->
                <v-btn text small color="primary" class="px-1" @click="addStringRule" data-testid="add-rule__str-add-btn">
                  <v-icon small class="mr-1">mdi-plus</v-icon>Add Rule
                </v-btn>
              </div>
            </div>

            <div v-for="(rule, idx) in stringRules" :key="rule.id" class="mb-2">
              <v-card outlined class="rounded-lg overflow-hidden">
                <div class="add-rule-str-grid pa-3 align-center">
                  <lb-dropdown :items="entity1StringCols" v-model="rule.leftField"  label="Field"      hidedetails class="add-rule-str-min-field" data-testid="add-rule__str-left-field" />
                  <lb-dropdown :items="strmatchtype"      v-model="rule.op"         label="Type"       hidedetails class="" @change="triggerReactivity"            data-testid="add-rule__str-op" />
                  <lb-dropdown :items="entity2StringCols" v-model="rule.rightField" label="With Field" hidedetails class="" data-testid="add-rule__str-right-field" />
                  <v-btn icon small color="grey" @click="removeStringRule(idx)" data-testid="add-rule__str-remove-btn">
                    <img src="/img/icons/tm-icons/Trash.svg" alt="Delete" data-testid="add-rule__str-remove-icon"/>
                  </v-btn>
                </div>
                <v-expand-transition>
                  <div v-if="isPatternMatch(rule.op)" class="add-rule-extras-row">
                    <!-- <v-divider class="flex-grow-1" /> -->
                    <lb-dropdown :items="getInColumnOptions(rule)" v-model="rule.inColumn" label="In Column" hidedetails class="add-rule-str-min-field" data-testid="add-rule__str-in-column" />
                    <div v-if="isApproxMatch(rule.op)" class="add-rule-match-percent-row">
                      <lb-number v-model="rule.matchPercent" min="1" max="100" prefix="Match" suffix="%" hidedetails class="add-rule-match-percent-input" data-testid="add-rule__str-match-percent" />
                    </div>
                    <!-- <v-divider class="flex-grow-1" /> -->
                  </div>
                </v-expand-transition>
              </v-card>
            </div>
          </div>

          <v-divider class="mb-5" />

          <!-- Number & Date Rules -->
          <div class="mb-5">
            <div class="d-flex align-center justify-space-between mb-3">
              <div>
                <div class=" font-weight-semibold">Number &amp; Date Matching</div>
                <!-- <div class=" grey--text mt-1">For amounts, dates, and numeric fields</div> -->
              </div>
              <v-btn text small color="primary" class="px-1" @click="addNumberRule" data-testid="add-rule__num-add-btn">
                <v-icon small class="mr-1">mdi-plus</v-icon>Add Rule
              </v-btn>
            </div>

            <div v-for="(rule, idx) in numberRules" :key="rule.id" class="mb-2">
              <v-card outlined class="rounded-lg overflow-hidden">
                <div class="add-rule-num-grid pa-3 align-center">
                  <lb-dropdown :items="entity1NumberCols" v-model="rule.leftField" label="Field" hidedetails class="" data-testid="add-rule__num-left-field" @change="onNumberFieldChange(rule)" />
                  <!-- <lb-dropdown :items="numberOps"         v-model="rule.op"         label="Match Type" hidedetails class="mr-2" data-testid="add-rule__num-op" /> -->
                  <span>With</span>
                  <lb-dropdown :items="entity2NumberCols" v-model="rule.rightField" label="Select Field" hidedetails class="" data-testid="add-rule__num-right-field" />
                  <div class="add-rule-tol-wrapper">
                    <label class="add-rule-tol-label">{{getToleranceUnits(rule.leftField)[0].text}}</label>
                    <lb-number type="number" :placeholder="getToleranceUnits(rule.leftField)[0].text" :value="rule.tolerance" @input="rule.tolerance = Number($event)" :min="isDateField(rule.leftField) ? undefined : '0'" class="add-rule-tol-input" data-testid="add-rule__num-tolerance-input" />
                  </div>
                  <v-btn icon small color="grey" @click="removeNumberRule(idx)" data-testid="add-rule__num-remove-btn">
                    <img src="/img/icons/tm-icons/Trash.svg" alt="Delete" data-testid="add-rule__num-remove-icon"/>
                  </v-btn>
                </div>
                <v-expand-transition>
                  <div v-if="rule.leftField && !isDateField(rule.leftField)" class="add-rule-num-sub-row px-3 pb-3 pt-3">
                    <!-- <v-divider class="mb-3" /> -->
                    <div class="d-flex align-start flex-column gap-4 flex-wrap">
                      <lb-dropdown
                        v-if="showGroupingSection && aggregationtype && aggregationtype.length"
                        :items="aggregationtype"
                        v-model="rule.aggregationMethod"
                        :label="isAggMandatory ? 'Aggregation Method *' : 'Aggregation Method'"
                        hidedetails
                        style="max-width:220px; flex:1;"
                        data-testid="add-rule__num-agg-method"
                      />
                      <v-checkbox
                        v-if="rule.leftField && !isDateField(rule.leftField)"
                        v-model="rule.oppositeSign"
                        dense
                        hide-details
                        label="Opposite sign match"
                        class="mt-0 pt-0 flex-shrink-0"
                        data-testid="add-rule__num-opposite-sign"
                      />
                    </div>
                  </div>
                </v-expand-transition>
              </v-card>
            </div>
          </div>

        </div>
      </div>
    </template>

    <!--  Footer Actions  -->
    <template v-slot:actions>
      <template v-if="currentStep === 1">
        <span class=" grey--text">*Required</span>
        <v-spacer />
        <base-button
          primary
          label="Next"
          append-icon="mdi-arrow-right"
          :disabled="!canProceedStep1"
          @click="goToStep2"
          data-testid="add-rule__next-btn"
        />
      </template>
      <template v-else>
        <base-button
          text
          label="Back"
          prepend-icon="mdi-arrow-left"
          @click="currentStep = 1"
          data-testid="add-rule__back-btn"
        />
        <v-spacer />
        <base-button
          v-if="addeditdata.__type === 'add'"
          primary
          label="Add Rule"
          @click="handleSubmit('add')"
          data-testid="add-rule__submit-btn"
        />
        <base-button
          v-else
          primary
          label="Save Rule"
          @click="handleSubmit('edit')"
          data-testid="add-rule__submit-btn"
        />
      </template>
    </template>
  </lb-drawer>
</template>

<script>
export default {
  name: 'AddRuleDialogV2',

  props: {
    value:                 { type: Boolean, default: false },
    addeditdata:           { type: Object,  default: () => ({}) },
    loading:               { type: Boolean, default: false },
    rulenaturelist:        { type: Array,   default: () => [] },
    ruletypelist:          { type: Array,   default: () => [] },
    columns:               { type: Object,  default: () => ({}) },
    selectedrulecolumn:    { type: Array,   default: () => [] },
    addeditdatacols:       { type: Object,  default: () => ({}) },
    strmatchtype:          { type: Array,   default: () => [] },
    filenames:             { type: Array,   default: () => [] },
    isAggregationMandatory:{ type: Boolean, default: false },
    aggregationtype:       { type: Array,   default: () => [] },
    aggregationtypesection:{ type: Object,  default: () => ({}) },
    aggregationError:      { type: String,  default: '' },
    // Already-loaded setup data from parent (avoids duplicate API call)
    setupList:             { type: Object,  default: () => ({}) },
  },

  data() {
    return {
      currentStep: 1,
      analysisloading: false,
      localNature: '',
      localType: '1:1',
      entityColumnsMap: {},
      entities: [],
      fileconfig1: '',
      fileconfig2: '',
      lastRuleEntities: '',
      stringRules: [{ id: 1, leftField: '', op: 'exact',       rightField: '', matchPercent: 80, inColumn: '' }],
      numberRules: [{ id: 1, leftField: '', op: 'exact match', rightField: '', tolerance: 0, toleranceUnit: 'days', oppositeSign: false, aggregationMethod: '' }],
      selectedGroupingTags: new Set(),
      matchFlagDesc: {
        automated: 'System automatically matches transactions without intervention.',
        suggested:  'System suggests matches for review and manual approval.',
        Automated:  'System automatically matches transactions without intervention.',
        Suggested:  'System suggests matches for review and manual approval.',
      },
      numberOps: [
        { name: 'Exact match',        value: 'exact match' },
        { name: 'Within tolerance',   value: 'within tolerance' },
        { name: 'Greater than',       value: 'greater than' },
        { name: 'Less than',          value: 'less than' },
      ],
      toleranceUnits: [
        { text: 'days range', value: 'days' },
        { text: 'amount range',  value: 'amount' },
        { text: '%',    value: '%' },
      ],
      amountKeywords: ['amount', 'curr', 'currency', 'balance', 'debit', 'credit'],
    };
  },

  computed: {
    steps() {
      return [{ id: 1, label: 'Match Setup' }, { id: 2, label: 'Match Rules' }];
    },
    bothEntitiesSelected() {
      return !!this.fileconfig1 && !!this.fileconfig2;
    },
    canProceedStep1() {
      return this.bothEntitiesSelected && !!this.localNature && !!this.localType;
    },
    showGroupingSection() {
      return ['1:M', 'M:1', 'M:M'].includes(this.localType);
    },
    allColumns() {
      const seen = new Set();
      const result = [];
      Object.values(this.entityColumnsMap).forEach(cols => {
        cols.forEach(c => { if (!seen.has(c.value)) { seen.add(c.value); result.push(c); } });
      });
      return result;
    },
    entity1Columns() { return this.entityColumnsMap[this.fileconfig1] || this.allColumns; },
    entity2Columns() { return this.entityColumnsMap[this.fileconfig2] || this.allColumns; },
    entity1StringCols() {
      const filtered = this.entity1Columns.filter(c => c.type === 'string');
      return filtered.length ? filtered : this.entity1Columns;
    },
    entity2StringCols() {
      const filtered = this.entity2Columns.filter(c => c.type === 'string');
      return filtered.length ? filtered : this.entity2Columns;
    },
    entity1NumberCols() {
      return this.entity1Columns.filter(c => c.type === 'number' || c.type === 'date');
    },
    entity2NumberCols() {
      return this.entity2Columns.filter(c => c.type === 'number' || c.type === 'date');
    },
    amountGroupingFields() {
      const seen = new Set();
      Object.values(this.entityColumnsMap).forEach(cols => {
        cols.forEach(c => { if (c.type === 'number' || this.isAmountField(c.value)) seen.add(c.value); });
      });
      return [...seen];
    },
    inColumnOptions() {
      return this.allColumns.map((l, i) => {
        const name = typeof l === 'object' ? l.name : l;
        return { name: `${i + 1} - ${name}`, value: `${name}` };
      });
    },
    groupingTagOptions() {
      return this.allColumns.map(l => (typeof l === 'object' ? l.name : l));
    },
    selectedAmountGroupingFields() {
      return [...this.selectedGroupingTags].filter(t => this.isAmountGroupingField(t));
    },
    localSelectedRuleColumn: {
      get()    { return this.selectedrulecolumn; },
      set(val) { this.$emit('update:selectedrulecolumn', val); },
    },
    // Computed locally because parent's isAggregationMandatory relies on selectedrulecolumn
    // which is not updated in the V2 flow — so we derive it from internal state instead.
    isAggMandatory() {
      // Mandatory only for M:1, 1:M, M:M when aggregation dropdown is visible
      if (!this.showGroupingSection) return false;
      if (!this.aggregationtype || !this.aggregationtype.length) return false;
      return this.numberRules.some(r => r.leftField && !this.isDateField(r.leftField));
    },
  },

  watch: {
    value(newVal) {
      if (!newVal) return;
      this.currentStep = 1;
      this.localNature = this.addeditdata.nature || 'Automated';
      this.localType   = this.addeditdata.type   || '1:1';
      this.parseSetupData();

      if (this.addeditdata.__type === 'edit') {
        // Restore entity selection
        this.fileconfig1 = this.addeditdata.fileconfig1 || '';
        this.fileconfig2 = this.addeditdata.fileconfig2 || '';

        // Restore string rules
        const editCols = Array.isArray(this.addeditdata.cols) ? this.addeditdata.cols : [];
        const strCols  = editCols.filter(c => c.datatype === 'string');
        this.stringRules = strCols.length
          ? strCols.map((c, i) => ({
              id:           Date.now() + i,
              leftField:    c.file1Column  || '',
              op:           c.type         || 'exact',
              rightField:   c.file2Column  || '',
              matchPercent: c.matchratio   || 80,
              inColumn:     c.in           || '',
            }))
          : [{ id: Date.now(), leftField: '', op: 'exact', rightField: '', matchPercent: 80, inColumn: '' }];

        // Restore number rules (includes date rules which now save as datatype === 'date')
        const numCols = editCols.filter(c => c.datatype === 'number' || c.datatype === 'date');
        this.numberRules = numCols.length
          ? numCols.map((c, i) => ({
              id:                Date.now() + 100 + i,
              leftField:         c.file1Column       || '',
              op:                'exact match',
              rightField:        c.file2Column       || '',
              tolerance:         c.type              || 0,
              toleranceUnit:     c.toleranceUnit     || 'days',
              oppositeSign:      c.negative          || false,
              aggregationMethod: c.aggregationMethod || '',
            }))
          : [{ id: Date.now() + 1, leftField: '', op: 'exact match', rightField: '', tolerance: 0, toleranceUnit: 'days', oppositeSign: false, aggregationMethod: '' }];

        // Restore grouping
        this.selectedGroupingTags = new Set(this.addeditdata.group_by || []);
        this.lastRuleEntities = `${this.fileconfig1}|${this.fileconfig2}`;
      } else {
        this.fileconfig1 = '';
        this.fileconfig2 = '';
        this.stringRules = [{ id: Date.now(),     leftField: '', op: 'exact',       rightField: '', matchPercent: 80, inColumn: '' }];
        this.numberRules = [{ id: Date.now() + 1, leftField: '', op: 'exact match', rightField: '', tolerance: 0, toleranceUnit: 'days', oppositeSign: false, aggregationMethod: '' }];
        this.selectedGroupingTags = new Set();
        this.lastRuleEntities = '';
      }
    },
    setupList: {
      deep: true,
      handler() { this.parseSetupData(); },
    },
    localType(newType) {
      // Clear aggregation methods when switching to 1:1
      if (!['1:M', 'M:1', 'M:M'].includes(newType)) {
        this.numberRules.forEach(r => { r.aggregationMethod = ''; });
      }
    },
  },

  beforeDestroy() {
    this.entities        = [];
    this.entityColumnsMap = {};
    this.stringRules     = [];
    this.numberRules     = [];
    this.selectedGroupingTags = new Set();
    this.fileconfig1     = '';
    this.fileconfig2     = '';
  },

  methods: {
    //  Entity 
    entityLabel(id) {
      const e = this.entities.find(x => x.id === id);
      return e ? e.label : id;
    },
    handleEntityClick(id) {
      if (this.fileconfig1 === id) { this.fileconfig1 = ''; return; }
      if (this.fileconfig2 === id) { this.fileconfig2 = ''; return; }
      if (!this.fileconfig1)       { this.fileconfig1 = id; return; }
      if (!this.fileconfig2)       { this.fileconfig2 = id; return; }
      this.fileconfig1 = this.fileconfig2;
      this.fileconfig2 = id;
    },
    goToStep2() {
      const entityKey = `${this.fileconfig1}|${this.fileconfig2}`;
      if (entityKey !== this.lastRuleEntities) {
        this.stringRules = [{ id: Date.now(), leftField: '', op: 'exact', rightField: '', matchPercent: 80, inColumn: '' }];
        this.numberRules = [{ id: Date.now() + 1, leftField: '', op: 'exact match', rightField: '', tolerance: 0, toleranceUnit: 'days', oppositeSign: false, aggregationMethod: '' }];
        this.lastRuleEntities = entityKey;
      }
      this.currentStep = 2;
    },

    //  Match Setup 
    setLocalNature(val) { this.localNature = val; this.$forceUpdate(); },
    setLocalType(val)   { this.localType   = val; this.$forceUpdate(); },

    //  String rules 
    getInColumnOptions(rule) {
      const opts = [];
      if (rule.leftField)  opts.push({ name: rule.leftField,  value: rule.leftField });
      if (rule.rightField && rule.rightField !== rule.leftField)
        opts.push({ name: rule.rightField, value: rule.rightField });
      return opts;
    },
    addStringRule() {
      this.stringRules.push({ id: Date.now(), leftField: '', op: 'exact', rightField: '', matchPercent: 80, inColumn: '' });
    },
    removeStringRule(idx) { this.stringRules.splice(idx, 1); },
    isPatternMatch(op) {
      return ['contains', 'begins with', 'ends with', 'approximate match'].includes((op || '').toLowerCase());
    },
    isApproxMatch(op) {
      return (op || '').toLowerCase() === 'approximate match';
    },

    //  Number rules 
    addNumberRule() {
      this.numberRules.push({ id: Date.now(), leftField: '', op: 'exact match', rightField: '', tolerance: 0, toleranceUnit: 'days', oppositeSign: false, aggregationMethod: '' });
    },
    removeNumberRule(idx) { this.numberRules.splice(idx, 1); },
    incrementTolerance(rule) { rule.tolerance = Number(rule.tolerance) + 1; },
    decrementTolerance(rule) {
      const val = Number(rule.tolerance) - 1;
      rule.tolerance = rule.toleranceUnit === 'days' ? val : Math.max(0, val);
    },
    isAmountField(fieldName) {
      if (!fieldName) return false;
      const lower = fieldName.toLowerCase();
      return this.amountKeywords.some(k => lower.includes(k));
    },
    isDateField(fieldName) {
      if (!fieldName) return false;
      const col = this.entity1NumberCols.find(c => c.value === fieldName);
      return col ? col.type === 'date' : false;
    },
    getToleranceUnits(fieldName) {
      if (this.isDateField(fieldName)) {
        return [{ text: 'day range', value: 'days' }];
      }
      return [
        { text: 'amount range', value: 'amount' },
        { text: '%',   value: '%' },
      ];
    },
    onNumberFieldChange(rule) {
      const units = this.getToleranceUnits(rule.leftField);
      if (!units.some(u => u.value === rule.toleranceUnit)) {
        rule.toleranceUnit = units[0].value;
      }
    },
    isAmountGroupingField(tag) {
      return this.amountGroupingFields.includes(tag) || this.isAmountField(tag);
    },

    //  Grouping 
    toggleGroupingTag(tag) {
      const next = new Set(this.selectedGroupingTags);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      this.selectedGroupingTags = next;
    },

    //  Reactivity trigger 
    triggerReactivity() {
      const temp = [...this.localSelectedRuleColumn, '__tmp__'];
      temp.pop();
      this.$emit('update:selectedrulecolumn', temp);
    },

    //  Submit 
    handleSubmit(type) {
      const validStringRules = this.stringRules.filter(r => r.leftField && r.rightField);
      const validNumberRules = this.numberRules.filter(r => r.leftField && r.rightField);

      if (!validStringRules.length && !validNumberRules.length) {
        this.$store.commit('sbError', 'Add at least one rule with both fields selected', { root: true });
        return;
      }

      if (this.isAggMandatory) {
        const missingAgg = validNumberRules.some(r => !this.isDateField(r.leftField) && !r.aggregationMethod);
        if (missingAgg) {
          this.$store.commit('sbError', 'Aggregation Method is required for number rules', { root: true });
          return;
        }
      }

      const cols = [];
      validStringRules.forEach(r => cols.push({
        file1Column: r.leftField,
        file2Column: r.rightField,
        datatype:    'string',
        type:        r.op,
        negative:    false,
        in:          r.inColumn     || null,
        matchratio:  this.isApproxMatch(r.op) ? r.matchPercent : null,
      }));
      validNumberRules.forEach(r => cols.push({
        file1Column:       r.leftField,
        file2Column:       r.rightField,
        datatype:          this.isDateField(r.leftField) ? 'date' : 'number',
        type:              r.tolerance,
        toleranceUnit:     r.toleranceUnit     || 'days',
        negative:          r.oppositeSign,
        ...(this.showGroupingSection ? { aggregationMethod: r.aggregationMethod || '' } : {}),
      }));

      const rulePayload = {
        nature:      this.localNature,
        type:        this.localType,
        fileconfig1: this.fileconfig1,
        fileconfig2: this.fileconfig2,
        file1Name:   this.entityLabel(this.fileconfig1),
        file2Name:   this.entityLabel(this.fileconfig2),
        cols,
        ...(this.showGroupingSection ? {
          group_by: [...this.selectedGroupingTags],
          agg_cols: this.selectedAmountGroupingFields,
          agg_type: this.selectedAmountGroupingFields.map(f => this.aggregationtypesection[f] || ''),
        } : {}),
      };

      this.$emit('save-rule-v2', type, rulePayload);
    },

    //  Parse setup data 
    parseSetupData() {
      const fileConfigData = this.setupList?.fileconfig || {};
      if (!Object.keys(fileConfigData).length) return;

      const fileNameMap = {};
      (this.setupList?.file || []).forEach(f => { if (f._id) fileNameMap[f._id] = f.name; });

      const entityList = [];
      const columnsMap = {};

      const normalizeType = (t) => {
        if (!t) return 'string';
        const lower = t.toLowerCase();
        if (['number', 'numeric', 'integer', 'int', 'float', 'double', 'decimal', 'bigint'].includes(lower)) return 'number';
        if (['date', 'datetime', 'timestamp', 'time'].includes(lower)) return 'date';
        return 'string';
      };

      Object.entries(fileConfigData).forEach(([entityId, entity]) => {
        if (!entity) return;
        const label = fileNameMap[entityId] || fileNameMap[entity.file] || entity.name || entity.label || entity.filename || entityId;
        if (!entityList.some(e => e.id === entityId)) {
          entityList.push({ id: entityId, label });
        }
        const entityCols = [];
        if (Array.isArray(entity.columnmap)) {
          entity.columnmap.forEach(col => {
            const labelName = Array.isArray(col) ? (col[1] || col[0]) : (col.label || col.name || col.header);
            if (labelName && !entityCols.some(c => c.value === labelName)) {
              const rawType = Array.isArray(col) ? undefined : col.type;
              entityCols.push({ name: labelName, value: labelName, type: normalizeType(rawType) });
            }
          });
        }
        columnsMap[entityId] = entityCols;
      });

      this.entities = entityList;
      this.$set(this, 'entityColumnsMap', columnsMap);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AddRuleDialogV2.scss';
</style>
